import { useState, useEffect, useMemo, useRef } from 'react';
import api from '../api';
import CatLoader from '../components/CatLoader';

const fmt = (v, dec=0) => v==null||v===''||isNaN(v) ? '—' : Number(v).toLocaleString('ru-RU',{maximumFractionDigits:dec});
const fmtPct = (v) => v==null||isNaN(v) ? '—' : Number(v).toFixed(1)+'%';
const fmtPrice = (v) => v==null||isNaN(v) ? '—' : Number(v).toLocaleString('ru-RU',{maximumFractionDigits:0})+' ₽';
const pad = (n) => String(n).padStart(2,'0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
const todayMSK = () => new Date(new Date().toLocaleString('en-US',{timeZone:'Europe/Moscow'}));
const todayStr = () => toISO(todayMSK());
const fmtDate = (s) => { const d = new Date(s+'T00:00:00'); return `${pad(d.getDate())}.${pad(d.getMonth()+1)}`; };

const btnStyle = (active) => ({
  padding:'5px 12px', fontSize:12, fontWeight:600, borderRadius:8, cursor:'pointer',
  border: active ? '1px solid rgba(59,130,246,0.5)' : '1px solid rgba(255,255,255,0.08)',
  background: active ? 'rgba(59,130,246,0.18)' : 'rgba(255,255,255,0.04)',
  color: active ? '#60a5fa' : 'rgba(200,215,235,0.7)',
});
const iSt = { padding:'7px 12px', borderRadius:8, border:'1px solid rgba(255,255,255,0.08)', background:'rgba(255,255,255,0.04)', color:'var(--text)', fontSize:13, outline:'none' };

function PeriodSelector({ onChange }) {
  const [mode, setMode] = useState('7days');
  const today = todayMSK();
  const apply = (m) => {
    setMode(m);
    let f; const t = toISO(today);
    if (m === '7days') f = toISO(new Date(today.getTime() - 7*86400000));
    else if (m === '14days') f = toISO(new Date(today.getTime() - 14*86400000));
    else if (m === '30days') f = toISO(new Date(today.getTime() - 30*86400000));
    onChange(f, t);
  };
  useEffect(() => { apply('7days'); }, []); // eslint-disable-line
  return (
    <div style={{ display:'flex', gap:8 }}>
      {[['7days','7 дней'],['14days','14 дней'],['30days','30 дней']].map(([k,l]) => (
        <button key={k} onClick={()=>apply(k)} style={btnStyle(mode===k)}>{l}</button>
      ))}
    </div>
  );
}

// Список строк-метрик: key — вычисляется функцией getVal(dayObj), group — для фильтра кнопками
const ROWS = [
  { key:'price',  label:'Наша цена',          group:'Цена', fmt:fmtPrice, getVal:d=>d.price },
  { key:'spp',    label:'СПП+кошелёк',         group:'Цена', fmt:fmtPct,   getVal:d=>d.spp },
  { key:'clientPrice', label:'Цена покупателя', group:'Цена', fmt:fmtPrice, getVal:d=>d.clientPrice },

  { key:'showsAll', label:'Показы, всего', group:'Показы', getVal:d=>d.impressions||null },
  { key:'showsOrg', label:'органика',      group:'Показы', getVal:d=>(d.impressions&&d.showsAd!=null)?Math.max(0,d.impressions-d.showsAd):null },
  { key:'showsAd',  label:'реклама',       group:'Показы', getVal:d=>d.showsAd },

  { key:'trAll', label:'Переходы, всего', group:'Переходы', getVal:d=>d.transitionsAll },
  { key:'trOrg', label:'органика',        group:'Переходы', getVal:d=>d.transitionsOrg },
  { key:'trAd',  label:'реклама',         group:'Переходы', getVal:d=>d.transitionsAd },

  { key:'ctrAll', label:'CTR, общий', group:'CTR', fmt:fmtPct, getVal:d=>d.ctr||null },
  { key:'ctrOrg', label:'органика',    group:'CTR', fmt:fmtPct, getVal:d=>{const sh=d.impressions&&d.showsAd!=null?Math.max(0,d.impressions-d.showsAd):null;return (sh&&d.transitionsOrg)?Math.round(d.transitionsOrg/sh*100*100)/100:null;} },
  { key:'ctrAd',  label:'реклама',     group:'CTR', fmt:fmtPct, getVal:d=>(d.showsAd&&d.transitionsAd)?Math.round(d.transitionsAd/d.showsAd*100*100)/100:null },

  { key:'cartAll', label:'Корзины, всего', group:'Корзины', getVal:d=>d.cartsAll },
  { key:'cartOrg', label:'органика',       group:'Корзины', getVal:d=>d.cartsOrg },
  { key:'cartAd',  label:'реклама',        group:'Корзины', getVal:d=>d.cartsAd },

  { key:'cvShowCartAll', label:'Из показа в корзину, общая', group:'Конв. в корзину (показ)', fmt:fmtPct, getVal:d=>(d.impressions&&d.cartsAll)?Math.round(d.cartsAll/d.impressions*100*100)/100:null },
  { key:'cvShowCartOrg', label:'органика', group:'Конв. в корзину (показ)', fmt:fmtPct, getVal:d=>{const sh=d.impressions&&d.showsAd!=null?Math.max(0,d.impressions-d.showsAd):null;return (sh&&d.cartsOrg)?Math.round(d.cartsOrg/sh*100*100)/100:null;} },
  { key:'cvShowCartAd',  label:'реклама',  group:'Конв. в корзину (показ)', fmt:fmtPct, getVal:d=>(d.showsAd&&d.cartsAd)?Math.round(d.cartsAd/d.showsAd*100*100)/100:null },

  { key:'cart1000All', label:'Корзин на 1000 показов, всего', group:'Корзин на 1000 показов', getVal:d=>(d.impressions&&d.cartsAll)?Math.round(d.cartsAll/(d.impressions/1000)*100)/100:null },
  { key:'cart1000Org', label:'органика', group:'Корзин на 1000 показов', getVal:d=>{const sh=d.impressions&&d.showsAd!=null?Math.max(0,d.impressions-d.showsAd):null;return (sh&&d.cartsOrg)?Math.round(d.cartsOrg/(sh/1000)*100)/100:null;} },
  { key:'cart1000Ad',  label:'реклама',  group:'Корзин на 1000 показов', getVal:d=>(d.showsAd&&d.cartsAd)?Math.round(d.cartsAd/(d.showsAd/1000)*100)/100:null },

  { key:'cvClickCartAll', label:'Из клика в корзину, общая', group:'Конв. в корзину (клик)', fmt:fmtPct, getVal:d=>d.convTrCart!=null?d.convTrCart:(d.transitionsAll>0?Math.round(d.cartsAll/d.transitionsAll*100*100)/100:null) },
  { key:'cvClickCartOrg', label:'органика', group:'Конв. в корзину (клик)', fmt:fmtPct, getVal:d=>d.transitionsOrg>0?d.cartsOrg/d.transitionsOrg*100:null },
  { key:'cvClickCartAd',  label:'реклама',  group:'Конв. в корзину (клик)', fmt:fmtPct, getVal:d=>d.transitionsAd>0?d.cartsAd/d.transitionsAd*100:null },

  { key:'ordAll', label:'Заказы, всего', group:'Заказы', getVal:d=>d.ordersAll },
  { key:'ordOrg', label:'органика',      group:'Заказы', getVal:d=>d.ordersOrg },
  { key:'ordAd',  label:'реклама',       group:'Заказы', getVal:d=>d.ordersAd },

  { key:'buyoutAll', label:'Выкупы, всего', group:'Выкупы', getVal:d=>d.buyoutQty!=null?d.buyoutQty:null },
  { key:'cancelsAll', label:'Отмены, всего', group:'Выкупы', getVal:d=>d.cancelsQty!=null?d.cancelsQty:null },
  { key:'buyoutPctAll', label:'Процент выкупа', group:'Выкупы', fmt:fmtPct, getVal:d=>d.buyoutPct!=null?d.buyoutPct:null },
  { key:'ordersSum', label:'Сумма заказов ₽', group:'Суммы', fmt:fmtPrice, getVal:d=>d.ordersSum!=null?d.ordersSum:null },
  { key:'buyoutSum', label:'Сумма выкупов ₽', group:'Суммы', fmt:fmtPrice, getVal:d=>d.buyoutSum!=null?d.buyoutSum:null },
  { key:'cvShowOrdAll', label:'Из показа в заказ, общая', group:'Конв. в заказ (показ)', fmt:fmtPct, getVal:d=>(d.impressions&&d.ordersAll)?Math.round(d.ordersAll/d.impressions*100*100)/100:null },
  { key:'cvShowOrdOrg', label:'органика', group:'Конв. в заказ (показ)', fmt:fmtPct, getVal:d=>{const sh=d.impressions&&d.showsAd!=null?Math.max(0,d.impressions-d.showsAd):null;return (sh&&d.ordersOrg)?Math.round(d.ordersOrg/sh*100*100)/100:null;} },
  { key:'cvShowOrdAd',  label:'реклама',  group:'Конв. в заказ (показ)', fmt:fmtPct, getVal:d=>(d.showsAd&&d.ordersAd)?Math.round(d.ordersAd/d.showsAd*100*100)/100:null },

  { key:'ord1000All', label:'Заказов на 1000 показов, всего', group:'Заказов на 1000 показов', getVal:d=>(d.impressions&&d.ordersAll)?Math.round(d.ordersAll/(d.impressions/1000)*100)/100:null },
  { key:'ord1000Org', label:'органика', group:'Заказов на 1000 показов', getVal:d=>{const sh=d.impressions&&d.showsAd!=null?Math.max(0,d.impressions-d.showsAd):null;return (sh&&d.ordersOrg)?Math.round(d.ordersOrg/(sh/1000)*100)/100:null;} },
  { key:'ord1000Ad',  label:'реклама',  group:'Заказов на 1000 показов', getVal:d=>(d.showsAd&&d.ordersAd)?Math.round(d.ordersAd/(d.showsAd/1000)*100)/100:null },

  { key:'cvClickOrdAll', label:'Из клика в заказ, общая', group:'Конв. в заказ (клик)', fmt:fmtPct, getVal:d=>d.transitionsAll>0?d.ordersAll/d.transitionsAll*100:null },
  { key:'cvClickOrdOrg', label:'органика', group:'Конв. в заказ (клик)', fmt:fmtPct, getVal:d=>d.transitionsOrg>0?d.ordersOrg/d.transitionsOrg*100:null },
  { key:'cvClickOrdAd',  label:'реклама',  group:'Конв. в заказ (клик)', fmt:fmtPct, getVal:d=>d.transitionsAd>0?d.ordersAd/d.transitionsAd*100:null },

  { key:'cvCartOrdAll', label:'Из корзины в заказ, общая', group:'Конв. из корзины в заказ', fmt:fmtPct, getVal:d=>d.convCartOrd!=null?d.convCartOrd:(d.cartsAll>0?Math.round(d.ordersAll/d.cartsAll*100*100)/100:null) },
  { key:'cvCartOrdOrg', label:'органика', group:'Конв. из корзины в заказ', fmt:fmtPct, getVal:d=>d.cartsOrg>0?d.ordersOrg/d.cartsOrg*100:null },
  { key:'cvCartOrdAd',  label:'реклама',  group:'Конв. из корзины в заказ', fmt:fmtPct, getVal:d=>d.cartsAd>0?d.ordersAd/d.cartsAd*100:null },
];

function ArticleCard({ a, dates, onHide }) {
  const [expanded, setExpanded] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const groups = [...new Set(ROWS.map(r => r.group))];
  const visibleRows = ROWS.filter(r => activeGroup === null || r.group === activeGroup);

  const dayData = dates.map(d => a.days[d] || {});
  const totalTr = dayData.reduce((s,d)=>s+(d.transitionsAll||0),0);
  const totalCart = dayData.reduce((s,d)=>s+(d.cartsAll||0),0);
  const totalOrd = dayData.reduce((s,d)=>s+(d.ordersAll||0),0);

  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12 }}>
      <div onClick={() => setExpanded(p=>!p)} style={{
        padding:'14px 20px', cursor:'pointer', display:'flex', alignItems:'center', gap:16,
        background: expanded ? 'rgba(255,255,255,0.03)' : 'transparent',
      }}>
        <div style={{ width:44, height:44, borderRadius:8, flexShrink:0, background:'var(--border)', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', position:'relative' }}>
          {a.photo
            ? <img src={a.photo} alt="" className="wb-photo" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
            : <span style={{ fontSize:10, color:'var(--text-muted)' }}>нет</span>}
          <button onClick={e => { e.stopPropagation(); onHide(a.nmId); }} style={{
            position:'absolute', bottom:1, right:1, background:'rgba(239,68,68,0.7)', border:'none', cursor:'pointer',
            fontSize:11, padding:'1px 3px', lineHeight:1, color:'#fff', fontWeight:700, borderRadius:3,
          }}>×</button>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:15, marginBottom:2 }}>{a.article}</div>
          <div style={{ fontSize:11, color:'var(--text-muted)' }}><span className="blur-nmid">{a.nmId}</span></div>
        </div>
        <div style={{ display:'flex', gap:20, alignItems:'center', flexShrink:0 }}>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:11, color:'var(--text-muted)' }}>Переходы</div>
            <div style={{ fontSize:14, fontWeight:600 }}>{fmt(totalTr)}</div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:11, color:'var(--text-muted)' }}>Корзины</div>
            <div style={{ fontSize:14, fontWeight:600 }}>{fmt(totalCart)}</div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:11, color:'var(--text-muted)' }}>Заказы</div>
            <div style={{ fontSize:14, fontWeight:600 }}>{fmt(totalOrd)}</div>
          </div>
          <div style={{ color:'var(--text-muted)', fontSize:16 }}>{expanded ? '▲' : '▼'}</div>
        </div>
      </div>

      {expanded && (
        <div style={{ borderTop:'1px solid var(--border)' }}>
          <div style={{ padding:'10px 20px', display:'flex', gap:8, flexWrap:'wrap', borderBottom:'1px solid var(--border)' }}>
            <button onClick={()=>setActiveGroup(null)} style={{ padding:'4px 12px', borderRadius:20, fontSize:12, cursor:'pointer', border:'1px solid var(--border)', background: activeGroup===null?'var(--accent)':'transparent', color: activeGroup===null?'#fff':'var(--text-muted)' }}>Все</button>
            {groups.map(g => (
              <button key={g} onClick={()=>setActiveGroup(ag=>ag===g?null:g)} style={{ padding:'4px 12px', borderRadius:20, fontSize:12, cursor:'pointer', border:'1px solid var(--border)', background: activeGroup===g?'var(--accent)':'transparent', color: activeGroup===g?'#fff':'var(--text-muted)' }}>{g}</button>
            ))}
          </div>
          <div style={{ overflow:'auto', maxHeight:500 }}>
            <div style={{ minWidth:'max-content' }}>
              <div style={{ display:'flex', position:'sticky', top:0, zIndex:10, background:'var(--surface)', borderBottom:'1px solid var(--border)' }}>
                <div style={{ width:230, flexShrink:0, padding:'8px 20px', fontWeight:600, fontSize:12, color:'var(--text-muted)', position:'sticky', left:0, background:'var(--surface)', zIndex:11, boxShadow:'2px 0 4px rgba(0,0,0,0.2)' }}>Показатель</div>
                {dates.map(d => (
                  <div key={d} style={{ width:72, flexShrink:0, padding:'8px 12px', textAlign:'right', fontSize:12, fontWeight:500, color: d===dates[dates.length-1]&&d===todayStr()?'rgba(150,165,180,0.5)':'var(--text-muted)', whiteSpace:'nowrap', background: d===dates[dates.length-1]&&d===todayStr()?'rgba(255,255,255,0.05)':'transparent' }}>
                    {fmtDate(d)}{d===dates[dates.length-1]&&d===todayStr()?' ●':''}
                  </div>
                ))}
              </div>
              {visibleRows.map((row, idx) => {
                const prevGroup = idx > 0 ? visibleRows[idx-1].group : null;
                const isNewGroup = row.group !== prevGroup;
                const fmtFn = row.fmt || fmt;
                const bg = idx % 2 === 0 ? 'var(--surface)' : 'rgba(17,24,39,0.98)';
                return (
                  <div key={row.key}>
                    {isNewGroup && (
                      <div style={{ display:'flex', background:'rgba(59,130,246,0.05)' }}>
                        <div style={{ width:230, flexShrink:0, padding:'8px 20px 4px', fontSize:11, fontWeight:700, color:'var(--accent)', textTransform:'uppercase', letterSpacing:'0.05em', position:'sticky', left:0, background:'rgba(59,130,246,0.05)' }}>{row.group}</div>
                        {dates.map(d => <div key={d} style={{ width:72, flexShrink:0, background:'rgba(59,130,246,0.05)' }} />)}
                      </div>
                    )}
                    <div style={{ display:'flex', borderBottom:'1px solid rgba(255,255,255,0.04)', background: idx%2===0?'transparent':'rgba(255,255,255,0.01)' }}>
                      <div style={{ width:230, flexShrink:0, padding:'7px 20px', fontSize:12, color: row.noApi?'rgba(200,215,235,0.3)':'var(--text-muted)', whiteSpace:'nowrap', position:'sticky', left:0, background:bg, boxShadow:'2px 0 4px rgba(0,0,0,0.2)', zIndex:1 }}>{row.label}</div>
                      {dayData.map((d, di) => {
                        const val = row.getVal(d);
                        const isLast = di === dayData.length-1 && dates[dates.length-1]===todayStr();
                        return (
                          <div key={di} style={{ width:72, flexShrink:0, padding:'7px 12px', textAlign:'right', fontSize:12,
                            color: row.noApi ? 'rgba(200,215,235,0.25)' : (isLast ? 'rgba(150,165,180,0.4)' : 'var(--text)'),
                            background: isLast ? 'rgba(255,255,255,0.05)' : 'transparent' }}>
                            {row.noApi ? '—' : fmtFn(val)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SalesFunnel() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [articles, setArticles] = useState([]);
  const [hidden, setHidden] = useState(new Set());
  const [showHidden, setShowHidden] = useState(false);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const funnelFileRef = useRef(null);
  const [importingFunnel, setImportingFunnel] = useState(false);

  const load = async (f, t) => {
    setLoading(true);
    try {
      const [r, hiddenR] = await Promise.all([
        api.get(`/funnel/data?from=${f}&to=${t}`),
        api.get('/funnel/hidden'),
      ]);
      setArticles(r.data.articles || []);
      setHidden(new Set((hiddenR.data || []).map(String)));
    } catch(e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };

  const handlePeriod = (f, t) => { setFrom(f); setTo(t); load(f, t); };
  const handleImportFunnel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportingFunnel(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/funnel/import-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert('Воронка импортирована: ' + r.data.date + ', обновлено ' + r.data.updated + ' артикулов');
      load(from, to);
    } catch(e) { alert('Ошибка: ' + (e.response?.data?.error || e.message)); }
    finally { setImportingFunnel(false); e.target.value = ''; }
  };

  const toggleHidden = async (nmId) => {
    const id = String(nmId);
    if (hidden.has(id)) {
      await api.delete(`/funnel/hidden/${nmId}`);
      setHidden(prev => { const n = new Set(prev); n.delete(id); return n; });
    } else {
      await api.post(`/funnel/hidden/${nmId}`);
      setHidden(prev => new Set(prev).add(id));
    }
  };

  const dates = useMemo(() => {
    if (!from || !to) return [];
    const d = []; let cur = new Date(from); const end = new Date(to);
    while (cur <= end) { d.push(toISO(cur)); cur.setDate(cur.getDate()+1); }
    return d;
  }, [from, to]);

  const filtered = useMemo(() => {
    let list = articles;
    list = list.filter(a => showHidden ? hidden.has(String(a.nmId)) : !hidden.has(String(a.nmId)));
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(a => (a.article||'').toLowerCase().includes(s) || String(a.nmId).includes(s));
    }
    return list;
  }, [articles, hidden, showHidden, search]);

  return (
    <div style={{ padding:'24px 32px', maxWidth:1200 }}>
      <div style={{ marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, marginBottom:4 }}>Воронка продаж</h1>
          <div style={{ fontSize:13, color:'var(--text-muted)' }}>
            Показы, переходы, корзины, заказы — органика и реклама · {from && to ? `${from} — ${to}` : ''}
          </div>
        </div>
        <PeriodSelector onChange={handlePeriod} />
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:18, alignItems:'center', flexWrap:'wrap' }}>
        <input placeholder="Поиск по артикулу..." value={search} onChange={e=>setSearch(e.target.value)} style={{ ...iSt, width:280 }} />
        <button onClick={()=>funnelFileRef.current.click()} title="Excel-файл из ЛК WB: Аналитика → Воронка продаж → Выгрузить (за 1 день)" style={{ background:'rgba(168,85,247,0.15)', border:'1px solid rgba(168,85,247,0.3)', color:'#a855f7', fontSize:12, padding:'7px 16px', borderRadius:8, cursor:'pointer' }}>
          {importingFunnel ? '⟳ Импорт...' : '+ Импорт воронки'}
        </button>
        <input type="file" accept=".xlsx" ref={funnelFileRef} style={{display:'none'}} onChange={handleImportFunnel}/>
        <button onClick={()=>setShowHidden(s=>!s)} style={btnStyle(showHidden)}>
          {showHidden ? `Скрытые (${hidden.size})` : 'Показать скрытые'}
        </button>
      </div>

      {loading && <CatLoader loading={true}/>}
      {!loading && error && <div style={{ padding:32, color:'var(--red)' }}>Ошибка: {error}</div>}
      {!loading && !error && filtered.length === 0 && (
        <div style={{ textAlign:'center', padding:64, color:'var(--text-muted)', fontSize:15 }}>
          {showHidden ? 'Нет скрытых артикулов' : 'Нет данных за выбранный период'}
        </div>
      )}
      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        {!loading && !error && filtered.map(a => <ArticleCard key={a.nmId} a={a} dates={dates} onHide={toggleHidden} />)}
      </div>

      {!loading && !error && filtered.length > 0 && (
        <div style={{ fontSize:11, color:'rgba(200,215,235,0.35)', marginTop:14, padding:'10px 14px',
          background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:8 }}>
          ⚠️ Метрика «Показы» недоступна ни в одном публичном API Wildberries — только вручную через Excel-выгрузку воронки в личном кабинете.
        </div>
      )}
    </div>
  );
}
